import { CashComparison } from './CashComparison';
import { formatAccountingInstant } from '../../../lib/accounting-time';
import { useAccountingDisplayTimezone } from '../../../hooks/useAccountingDisplayTimezone';
import { PETTY_CREATE_PURPOSES } from '@pokerroom/types';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  CashWorkflowReceiptV1,
  CashWorkflowSnapshotV1,
  CashWorkflowWriteV1,
  CashOperationLookupV1,
} from '@pokerroom/types';
import { api } from '../../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../../store/auth.store';
import { useCashIntent, useCashScope } from './useCashIntent';
import type { CashIntent } from './cashRecovery';
import { CashRecoveryNotice } from './CashRecoveryNotice';

const independentHandoffs = new Set([
  'ZIPA_FEE_HANDOFF',
  'TIMED_ENTRY_HANDOFF',
  'ZIPA_AWARD_HANDOFF',
]);
const daily = ['ZIPA_DRAWER', 'RESERVE', 'PETTY'];
const buttonClass =
  'min-h-11 rounded-md border border-base-300 bg-base-100 px-4 py-2 text-sm disabled:opacity-50';
const inputClass = 'min-h-11 rounded-md border border-base-300 bg-base-100 px-3 py-2';
const validMoney = (v: string) => /^\d{1,10}(\.\d{1,2})?$/.test(v);
export function CashWorkflowPanel({
  sessionId,
  redemptionMemberId,
  surface,
  accountingLayout = false,
  parentReady = true,
  parentRefreshing = false,
  parentContextKey,
}: {
  surface: 'registration' | 'settlement' | 'redemption';
  sessionId: string;
  redemptionMemberId?: string;
  accountingLayout?: boolean;
  parentReady?: boolean;
  parentRefreshing?: boolean;
  parentContextKey?: string;
}) {
  const { t, i18n } = useTranslation('accounting');
  const redemptionInputClass =
    'block min-h-11 w-full rounded border border-base-300 bg-base-100 p-2';
  const redemptionButtonClass =
    'min-h-11 rounded border border-base-300 px-3 py-2 disabled:opacity-50';
  const displayTimezone = useAccountingDisplayTimezone();
  const registering = surface === 'registration';
  const settling = surface === 'settlement';
  const owns = (action: string) =>
    action === 'CLOSE_LOCATION_SESSION' || action === 'CASH_HANDOFF'
      ? settling
      : action === 'CASH_REVENUE_HANDOFF'
        ? registering
        : action === 'CASH_ZIPA_REDEMPTION'
          ? surface === 'redemption'
          : registering;
  const qc = useQueryClient();
  const scope = `${useCashScope(redemptionMemberId)}:${sessionId}`;
  const identity = useAuthStore((s) => JSON.stringify([s.sessionRevision, s.allowedActions]));
  const identityRef = useRef(scope + identity);
  identityRef.current = scope + identity;
  const canRead = useAuthStore((s) => s.allowedActions?.includes('accounting:read') === true);
  const canEdit = useAuthStore((s) => s.allowedActions?.includes('accounting:edit') === true);
  const canRedeem = useAuthStore(
    (s) => s.allowedActions?.includes('member:adjust_points') === true,
  );
  const [redemptionAmount, setRedemptionAmount] = useState('');
  const [redemptionEvidence, setRedemptionEvidence] = useState('');
  const [redemptionConfirmed, setRedemptionConfirmed] = useState(false);
  const canClose = useAuthStore((s) => s.allowedActions?.includes('accounting:close') === true);
  const cash = useCashIntent(scope, 'workflow');
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [pettyAmount, setPettyAmount] = useState('');
  const [purpose, setPurpose] = useState<
    (typeof PETTY_CREATE_PURPOSES)[number] | 'FUND_TRANSFER_IN' | 'RESERVE_TOPUP' | 'EXTERNAL_TOPUP'
  >('STATIONERY_CLEANING');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [sourceDescription, setSourceDescription] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [pettyConfirmed, setPettyConfirmed] = useState(false);
  const [handoffSelection, setHandoffSelection] = useState<string | null>(null);
  const [handoffConfirmed, setHandoffConfirmed] = useState(false);
  const [handoffNote, setHandoffNote] = useState('');
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [receipt, setReceipt] = useState<CashWorkflowReceiptV1 | null>(null);
  const [readyKey, setReadyKey] = useState('');
  const snapshot = useQuery({
    queryKey: settling
      ? ['cash-workflow', scope, identity, 'settlement']
      : ['cash-workflow', scope, identity],
    staleTime: settling ? 0 : undefined,
    refetchOnMount: settling ? 'always' : undefined,
    enabled: canRead,
    retry: false,
    refetchOnWindowFocus: 'always',
    queryFn: ({ signal }) =>
      api.get<CashWorkflowSnapshotV1>(
        `/accounting/cash-workflow/${encodeURIComponent(sessionId)}`,
        { signal },
      ),
  });
  const settlementSnapshotReady =
    !settling || (!snapshot.isError && !snapshot.isFetching && snapshot.data?.status === 'OPEN');
  useEffect(() => {
    setRedemptionAmount('');
    setRedemptionEvidence('');
    setRedemptionConfirmed(false);
    setCounts({});
    setPettyAmount('');
    setEvidenceRef('');
    setSourceDescription('');
    setTopupNote('');
    setPurpose('STATIONERY_CLEANING');
    setPettyConfirmed(false);
    setNote('');
    setConfirmed(false);
    setReceipt(null);
    setReadyKey('');
  }, [scope, identity]);
  useEffect(() => {
    setConfirmed(false);
    setPettyConfirmed(false);
    setRedemptionConfirmed(false);
  }, [parentContextKey]);
  useEffect(() => {
    setConfirmed(false);
    setReadyKey('');
    setPettyConfirmed(false);
    setRedemptionConfirmed(false);
    if (snapshot.data?.status === 'OPEN') setCounts({});
  }, [snapshot.data?.version, snapshot.data?.status]);
  useEffect(() => {
    if (snapshot.data?.status === 'CLOSED' && snapshot.data.closingCoverage)
      setCounts(
        Object.fromEntries(
          snapshot.data.closingCoverage.lines.map((l) => [l.locationId, l.amount]),
        ),
      );
  }, [snapshot.data]);
  useEffect(() => {
    setHandoffSelection(null);
    setHandoffConfirmed(false);
    setHandoffNote('');
  }, [scope, identity, parentContextKey, snapshot.data?.version, snapshot.data?.status]);
  async function refreshResult() {
    const refreshed = await snapshot.refetch();
    if (
      refreshed.isError ||
      !refreshed.data ||
      refreshed.data.sessionId !== sessionId ||
      refreshed.data.venueId !== sessionVenueKey(useAuthStore.getState().accessToken)
    )
      throw new Error('Current snapshot unavailable');
    for (const key of [
      'daily-ledgers',
      'accounting-summary',
      'accounting-active-session',
      'accounting-session-context',
    ])
      await qc.invalidateQueries({ queryKey: [key] }, { throwOnError: true });
    if (redemptionMemberId) {
      for (const key of ['member-zipa', 'zipa-journal', 'member-ledger-summary', 'member'])
        await qc.invalidateQueries({ queryKey: [key, redemptionMemberId] }, { throwOnError: true });
      await qc.invalidateQueries({ queryKey: ['zipa-daily-totals'] }, { throwOnError: true });
      await qc.invalidateQueries(
        {
          queryKey: ['member-zipa-balances', sessionVenueKey(useAuthStore.getState().accessToken)],
        },
        { throwOnError: true },
      );
    }
  }
  function validReceipt(r: CashWorkflowReceiptV1, record: CashIntent) {
    return (
      r?.contractVersion === 1 &&
      r.status === 'COMMITTED' &&
      typeof r.operationId === 'string' &&
      !!r.operationId &&
      typeof r.businessDate === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(r.businessDate) &&
      typeof r.asOf === 'string' &&
      Number.isFinite(Date.parse(r.asOf)) &&
      typeof r.replayed === 'boolean' &&
      r.operationKey === record.operationKey &&
      r.sessionId === sessionId &&
      r.action === record.action &&
      r.snapshot?.venueId === sessionVenueKey(useAuthStore.getState().accessToken) &&
      r.snapshot.sessionId === sessionId &&
      r.snapshot.contractVersion === 1 &&
      r.snapshot.businessDate === r.businessDate &&
      ['OPEN', 'CLOSED'].includes(r.snapshot.status) &&
      typeof r.snapshot.version === 'string' &&
      !!r.snapshot.version &&
      Array.isArray(r.snapshot.bags) &&
      typeof r.snapshot.memberZipaBalance === 'string' &&
      /^-?\d+(\.\d{1,2})?$/.test(r.snapshot.memberZipaBalance) &&
      (r.action !== 'CASH_ZIPA_REDEMPTION' ||
        (!!redemptionMemberId && r.redemption?.memberId === redemptionMemberId))
    );
  }
  async function accept(r: CashWorkflowReceiptV1, record: CashIntent, consumed: boolean) {
    if (!cash.committed(record)) return;
    setReadyKey('');
    setReceipt(r);
    if (consumed) {
      if (record.action === 'CASH_HANDOFF') {
        setHandoffSelection(null);
        setHandoffConfirmed(false);
        setHandoffNote('');
      }
      if (
        record.action === 'CASH_PETTY_TRANSACTION' ||
        record.action === 'CASH_REPLENISH' ||
        record.action === 'CASH_PETTY_EXTERNAL_TOPUP'
      ) {
        setPettyAmount('');
        setEvidenceRef('');
        setSourceDescription('');
        setTopupNote('');
        setPettyConfirmed(false);
      }
      if (record.action === 'CASH_ZIPA_REDEMPTION') {
        setRedemptionAmount('');
        setRedemptionEvidence('');
        setRedemptionConfirmed(false);
        setNote('');
      }
      setCounts({});
      setConfirmed(false);
      if (record.action === 'CLOSE_LOCATION_SESSION') setNote('');
    }
    try {
      await refreshResult();
      if (identityRef.current !== scope + identity || !cash.isCurrent(record.operationKey)) return;
      setReadyKey(record.operationKey);
      cash.setError('');
    } catch {
      if (identityRef.current === scope + identity && cash.isCurrent(record.operationKey))
        cash.setError('REFRESH');
    }
  }
  async function submit(body: CashWorkflowWriteV1, retained?: CashIntent) {
    if (!parentReady || !settlementSnapshotReady) return;
    if (body.action === 'REVERSE_EVENT_RESERVE_HANDOFF') return;
    if (
      !owns(body.action) ||
      !canRead ||
      (body.action === 'CLOSE_LOCATION_SESSION' ? !canClose : !canEdit)
    )
      return;
    if (
      !retained &&
      (snapshot.isError ||
        snapshot.isFetching ||
        snapshot.data?.status !== 'OPEN' ||
        (cash.intent &&
          (cash.intent.stage !== 'COMMITTED' || readyKey !== cash.intent.operationKey)))
    )
      return;
    if (
      body.action === 'CASH_ZIPA_REDEMPTION' &&
      (!canRedeem || body.memberId !== redemptionMemberId)
    )
      return;
    const now = Date.now();
    const record: CashIntent = retained ?? {
      recordVersion: 1,
      scope,
      createdAt: now,
      lastSeenAt: now,
      action: body.action,
      operationKey: body.operationKey,
      sessionId,
      stage: 'UNKNOWN',
      body,
    };
    await cash.post<CashWorkflowReceiptV1>(
      record,
      '/accounting/cash-workflow',
      (r) => validReceipt(r, record),
      (r) => accept(r, record, true),
      retained?.operationKey ?? (readyKey || undefined),
    );
  }
  async function recover() {
    const record = cash.intent;
    if (!canRead || !record) return;
    await cash.run(async () => {
      const found = await api.get<CashOperationLookupV1>(
        `/accounting/operations/${encodeURIComponent(record.operationKey)}`,
      );
      if (!cash.isCurrent(record.operationKey)) return;
      if (
        found?.contractVersion !== 1 ||
        found.requestedOperationKey !== record.operationKey ||
        found.originalOperationKey !== record.operationKey ||
        found.action !== record.action ||
        !validReceipt(found.result as CashWorkflowReceiptV1, record) ||
        found.operationId !== (found.result as CashWorkflowReceiptV1).operationId
      ) {
        cash.setError('RECONCILE');
        return;
      }
      await accept(found.result as CashWorkflowReceiptV1, record, record.stage !== 'COMMITTED');
    });
  }
  useEffect(() => {
    if (canRead && cash.intent?.stage === 'COMMITTED') void recover();
  }, [scope, identity, canRead, cash.intent?.operationKey, cash.intent?.stage]);
  if (!canRead || cash.error === 'DENIED') return <p role="alert">{t('cashWorkflow.denied')}</p>;
  if (snapshot.isError)
    return (
      <p role="alert">
        {t('cashWorkflow.loadFailed')}{' '}
        <button onClick={() => void snapshot.refetch()}>{t('cashWorkflow.refresh')}</button>
      </p>
    );
  if (!snapshot.data) return <p role="status">{t('cashSummaryLoading')}</p>;
  const snap = snapshot.data;
  if (
    snap.venueId !== sessionVenueKey(useAuthStore.getState().accessToken) ||
    snap.sessionId !== sessionId
  )
    return <p role="alert">{t('cashWorkflow.denied')}</p>;
  const settlement = snap.dailySettlement;
  const kinds =
    settlement || snap.cashBagContractVersion === 2 ? [...daily, 'OPERATING_REVENUE'] : daily;
  const bags = kinds.flatMap((kind) =>
    (settlement?.predepositBags ?? snap.bags).filter((b) => b.kind === kind),
  );
  const validCounts =
    bags.length === kinds.length && bags.every((b) => validMoney(counts[b.id] ?? ''));
  const balanced =
    validCounts &&
    bags.every((b) => Number(counts[b.id]) === Number(b.amount)) &&
    Number(counts[bags.find((b) => b.kind === 'ZIPA_DRAWER')?.id ?? '']) ===
      Number(snap.memberZipaBalance);
  const reserve = snap.bags.find((b) => b.kind === 'RESERVE'),
    petty = snap.bags.find((b) => b.kind === 'PETTY');
  const fundTransfer = purpose === 'FUND_TRANSFER_IN';
  const reserveTopup = purpose === 'RESERVE_TOPUP';
  const externalTopup = purpose === 'EXTERNAL_TOPUP';
  const localNote = evidenceRef.trim();
  const base = () => ({
    operationKey: crypto.randomUUID(),
    sessionId,
    expectedVersion: snap.version,
    cashBagContractVersion: snap.cashBagContractVersion ?? 1,
    note: note.trim(),
  });
  const locked =
    !parentReady ||
    cash.busy ||
    snapshot.isError ||
    snapshot.isFetching ||
    (!!cash.intent &&
      (cash.intent.stage !== 'COMMITTED' || readyKey !== cash.intent.operationKey)) ||
    snap.status !== 'OPEN';
  return (
    <section
      className={`space-y-5 ${surface === 'redemption' ? 'w-full' : accountingLayout ? (settling ? 'accounting-main-form' : 'min-w-0') : 'max-w-3xl'}`}
      aria-label={t('cashWorkflow.title')}
      aria-busy={snapshot.isFetching || parentRefreshing || cash.busy}
    >
      {!registering && (
        <>
          {!accountingLayout && surface !== 'redemption' && (
            <h1 className="text-2xl font-bold">{t('cashWorkflow.title')}</h1>
          )}
          <p className={surface === 'redemption' ? 'text-sm text-base-content/70' : undefined}>
            {t('cashWorkflow.asOf', {
              date: snap.businessDate,
              time:
                formatAccountingInstant(snap.asOf, displayTimezone ?? '', i18n?.language) ??
                t('dailyLedgers.unavailable'),
            })}
          </p>
        </>
      )}
      {(snapshot.isFetching || parentRefreshing) && <p role="status">{t('cashSummaryLoading')}</p>}
      {cash.intent?.stage === 'COMMITTED' &&
        readyKey !== cash.intent.operationKey &&
        !cash.error && <p role="status">{t('cash.verifying')}</p>}
      {!redemptionMemberId && (
        <p>
          {t('cashWorkflow.externalTotal', {
            amount: snap.pettyExternalTopupTotal ?? t('counted.unavailable'),
          })}
        </p>
      )}
      {surface === 'settlement' && snap.status === 'OPEN' && <CashComparison snapshot={snap} />}
      {surface === 'settlement' && (
        <p>
          {t('cashWorkflow.memberTotal', {
            amount: snap.memberZipaBalance,
            count: snap.memberCount,
          })}
        </p>
      )}
      {redemptionMemberId && canEdit && canRedeem && (
        <div className="space-y-3">
          <p>{t('cashRedemption.help')}</p>
          <label className="block">
            {t('cashRedemption.amount')}
            <input
              className={redemptionInputClass}
              inputMode="decimal"
              value={redemptionAmount}
              disabled={locked}
              onChange={(e) => {
                setRedemptionAmount(e.target.value);
                setRedemptionConfirmed(false);
              }}
            />
          </label>
          <label className="block">
            {t('cashRedemption.evidence')}
            <input
              className={redemptionInputClass}
              maxLength={255}
              value={redemptionEvidence}
              disabled={locked}
              onChange={(e) => {
                setRedemptionEvidence(e.target.value);
                setRedemptionConfirmed(false);
              }}
            />
          </label>
          <label className="block">
            {t('cash.note')}
            <input
              className={redemptionInputClass}
              maxLength={255}
              value={note}
              disabled={locked}
              onChange={(e) => {
                setNote(e.target.value);
                setRedemptionConfirmed(false);
              }}
            />
          </label>
          <label className="flex gap-2 items-center">
            <input
              type="checkbox"
              checked={redemptionConfirmed}
              disabled={locked}
              onChange={(e) => setRedemptionConfirmed(e.target.checked)}
            />
            {t('cashRedemption.confirm')}
          </label>
          <button
            className={redemptionButtonClass}
            disabled={
              locked ||
              !redemptionConfirmed ||
              !validMoney(redemptionAmount) ||
              Number(redemptionAmount) <= 0 ||
              !redemptionEvidence.trim() ||
              !note.trim()
            }
            onClick={() =>
              void submit({
                ...base(),
                action: 'CASH_ZIPA_REDEMPTION',
                sourceRef: crypto.randomUUID(),
                memberId: redemptionMemberId,
                amount: redemptionAmount,
                evidenceRef: redemptionEvidence.trim(),
                confirmed: redemptionConfirmed,
              })
            }
          >
            {t('cashRedemption.submit')}
          </button>
        </div>
      )}
      {!redemptionMemberId && (
        <>
          {settling && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm [&_th]:px-2 [&_th]:py-3 [&_td]:px-2 [&_td]:py-3 [&_tr]:border-b [&_tr]:border-base-300">
                  <thead>
                    <tr>
                      <th>{t('cashWorkflow.bag')}</th>
                      <th>{t(settlement ? 'comparison.projected' : 'cashWorkflow.bookHeading')}</th>
                      <th>{t('cashWorkflow.counted')}</th>
                      <th>{t('cashWorkflow.difference')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bags.map((b) => (
                      <tr key={b.id}>
                        <th>{t(`cashWorkflow.bags.${b.kind}`)}</th>
                        <td className="text-right tabular-nums">{b.amount}</td>
                        <td>
                          <input
                            className={`${inputClass} w-full min-w-24 text-right tabular-nums`}
                            aria-label={t(`cashWorkflow.bags.${b.kind}`)}
                            inputMode="decimal"
                            disabled={cash.busy || snap.status === 'CLOSED'}
                            value={counts[b.id] ?? ''}
                            onChange={(e) => {
                              setCounts((old) => ({ ...old, [b.id]: e.target.value }));
                              setConfirmed(false);
                            }}
                          />
                        </td>
                        <td className="text-right tabular-nums">
                          {validMoney(counts[b.id] ?? '')
                            ? (Number(counts[b.id]) - Number(b.amount)).toFixed(2)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p role="status">
                {t('cashWorkflow.coverage', {
                  amount: validMoney(
                    counts[bags.find((b) => b.kind === 'ZIPA_DRAWER')?.id ?? ''] ?? '',
                  )
                    ? (
                        Number(counts[bags.find((b) => b.kind === 'ZIPA_DRAWER')!.id]) -
                        Number(snap.memberZipaBalance)
                      ).toFixed(2)
                    : '—',
                })}
              </p>
              <p>{t('cashWorkflow.varianceHelp')}</p>
            </>
          )}
          {settling && snap.status === 'OPEN' && (
            <label className="block">
              {t('cashWorkflow.note')}
              <input
                className={`${inputClass} w-full`}
                maxLength={255}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setConfirmed(false);
                }}
              />
            </label>
          )}
          {settling && (
            <div className="space-y-3">
              <p>
                {t('dailySettlement.topupTotal')}:{' '}
                <strong className="tabular-nums">
                  {settlement?.topupAmount ?? t('counted.unavailable')}
                </strong>
              </p>
              <p>
                {t('dailySettlement.safeDeposit')}:{' '}
                <strong className="tabular-nums">
                  {settlement?.complete ? settlement.remainingRevenue : t('counted.unavailable')}
                </strong>
              </p>
              <h2 className="font-bold">{t('cashWorkflow.handoffs')}</h2>
              <p>{t('dailySettlement.sourceHelp')}</p>
              <p>{t('cashWorkflow.sourceHandoff.help')}</p>
              {!canEdit && <p>{t('cashWorkflow.sourceHandoff.permission')}</p>}
              <ul className="space-y-2">
                {snap.handoffs.map((h) => {
                  const key = `${h.sourceType}:${h.sourceId}`;
                  const eligible = independentHandoffs.has(h.sourceType) && Number(h.amount) > 0;
                  const selected = handoffSelection === key;
                  const sourceBag = snap.bags.find((bag) =>
                    h.direction === 'ZIPA_TO_EVENT'
                      ? bag.kind === 'ZIPA_DRAWER'
                      : bag.kind === 'EVENT_BASKET' && bag.tournamentId === h.tournamentId,
                  );
                  const funded = !!sourceBag && Number(sourceBag.amount) >= Number(h.amount);
                  return (
                    <li
                      key={key}
                      className="break-words rounded border border-base-300 p-3 space-y-2"
                    >
                      <p>
                        {h.tournamentName} {h.memberName ?? ''}: {h.amount}{' '}
                        {h.completed
                          ? t('cashWorkflow.completed')
                          : t('dailySettlement.sourcePending')}
                      </p>
                      {eligible && (
                        <>
                          <p>
                            {t('cashWorkflow.sourceHandoff.source', {
                              date: snap.businessDate,
                              id: h.sourceId,
                            })}
                          </p>
                          <p>{t(`cashWorkflow.sourceHandoff.${h.direction}`)}</p>
                          {!h.completed && !funded && (
                            <p role="status">{t('cashWorkflow.sourceHandoff.fundsHelp')}</p>
                          )}
                          {!h.completed &&
                            snap.status === 'OPEN' &&
                            canEdit &&
                            (selected ? (
                              <div
                                className="space-y-3"
                                role="group"
                                aria-label={t('cashWorkflow.sourceHandoff.prepare')}
                              >
                                <label className="block">
                                  {t('cashWorkflow.sourceHandoff.note')}
                                  <input
                                    className={`${inputClass} w-full`}
                                    maxLength={255}
                                    value={handoffNote}
                                    disabled={locked}
                                    onChange={(e) => {
                                      setHandoffNote(e.target.value);
                                      setHandoffConfirmed(false);
                                    }}
                                  />
                                </label>
                                <label className="flex items-start gap-2">
                                  <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={handoffConfirmed}
                                    disabled={locked}
                                    onChange={(e) => setHandoffConfirmed(e.target.checked)}
                                  />
                                  <span>
                                    {t('cashWorkflow.sourceHandoff.confirm', { amount: h.amount })}
                                  </span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    className={buttonClass}
                                    disabled={
                                      locked || !funded || !handoffConfirmed || !handoffNote.trim()
                                    }
                                    onClick={() => {
                                      if (
                                        !handoffConfirmed ||
                                        !handoffNote.trim() ||
                                        locked ||
                                        !funded
                                      )
                                        return;
                                      void submit({
                                        ...base(),
                                        action: 'CASH_HANDOFF',
                                        sourceType: h.sourceType,
                                        sourceId: h.sourceId,
                                        note: handoffNote.trim(),
                                      });
                                    }}
                                  >
                                    {t('cashWorkflow.confirmHandoff')}
                                  </button>
                                  <button
                                    type="button"
                                    className={buttonClass}
                                    disabled={locked}
                                    onClick={() => {
                                      setHandoffSelection(null);
                                      setHandoffConfirmed(false);
                                      setHandoffNote('');
                                    }}
                                  >
                                    {t('cashWorkflow.sourceHandoff.cancel')}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className={buttonClass}
                                disabled={locked || !funded}
                                onClick={() => {
                                  setHandoffSelection(key);
                                  setHandoffConfirmed(false);
                                  setHandoffNote('');
                                }}
                              >
                                {t('cashWorkflow.sourceHandoff.prepare')}
                              </button>
                            ))}
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {snap.status === 'OPEN' && (
            <>
              {registering && (
                <>
                  <section
                    className="border border-base-300 rounded p-3 space-y-3"
                    aria-label={t('cashWorkflow.pettyTitle')}
                  >
                    <h2>{t('cashWorkflow.pettyTitle')}</h2>
                    <p>
                      {t(
                        reserveTopup
                          ? 'dailySettlement.topupHelp'
                          : externalTopup
                            ? 'cashWorkflow.externalHelp'
                            : fundTransfer
                              ? 'cashWorkflow.fundExplanation'
                              : 'cashWorkflow.pettyExplanation',
                      )}
                    </p>
                    <label className="block">
                      {t('cashWorkflow.purpose')}
                      <select
                        aria-label={t('cashWorkflow.purpose')}
                        className={`${inputClass} w-full`}
                        value={purpose}
                        disabled={locked}
                        onChange={(e) => {
                          setPurpose(e.target.value as typeof purpose);
                          setPettyConfirmed(false);
                        }}
                      >
                        {(
                          [
                            'FUND_TRANSFER_IN',
                            'EXTERNAL_TOPUP',
                            ...PETTY_CREATE_PURPOSES,
                            'RESERVE_TOPUP',
                          ] as const
                        ).map((v) => (
                          <option key={v} value={v}>
                            {t(`cashWorkflow.purpose_${v}`)}
                          </option>
                        ))}
                      </select>
                    </label>
                    {externalTopup && (
                      <>
                        <label className="block">
                          {t('cashWorkflow.externalSource')}
                          <input
                            className={`${inputClass} w-full`}
                            value={sourceDescription}
                            maxLength={255}
                            disabled={locked}
                            onChange={(e) => {
                              setSourceDescription(e.target.value);
                              setPettyConfirmed(false);
                            }}
                          />
                        </label>
                        <label className="block">
                          {t('cashWorkflow.externalNote')}
                          <input
                            className={`${inputClass} w-full`}
                            value={topupNote}
                            maxLength={255}
                            disabled={locked}
                            onChange={(e) => {
                              setTopupNote(e.target.value);
                              setPettyConfirmed(false);
                            }}
                          />
                        </label>
                      </>
                    )}
                    <label className="block">
                      {t('cashWorkflow.pettyAmount')}
                      <input
                        className={`${inputClass} w-full`}
                        inputMode="decimal"
                        value={pettyAmount}
                        disabled={locked}
                        onChange={(e) => {
                          setPettyAmount(e.target.value);
                          setPettyConfirmed(false);
                        }}
                      />
                    </label>
                    <label className="block">
                      {t(
                        reserveTopup
                          ? 'dailySettlement.topupNote'
                          : externalTopup
                            ? 'cashWorkflow.externalEvidence'
                            : 'cashWorkflow.evidenceRef',
                      )}
                      <input
                        required
                        aria-describedby="petty-evidence-help"
                        className={`${inputClass} w-full`}
                        maxLength={255}
                        value={evidenceRef}
                        disabled={locked}
                        onChange={(e) => {
                          setEvidenceRef(e.target.value);
                          setPettyConfirmed(false);
                        }}
                      />
                    </label>
                    <p id="petty-evidence-help" className="text-sm">
                      {t(
                        reserveTopup
                          ? 'dailySettlement.topupAvailable'
                          : externalTopup
                            ? 'cashWorkflow.externalEvidenceHelp'
                            : 'cashWorkflow.evidenceHelp',
                        {
                          amount:
                            settlement && settlement.topupAmount !== null
                              ? settlement.remainingRevenue
                              : t('counted.unavailable'),
                        },
                      )}
                    </p>
                    <label className="flex min-h-11 items-center gap-2">
                      <input
                        type="checkbox"
                        checked={pettyConfirmed}
                        disabled={locked}
                        onChange={(e) => setPettyConfirmed(e.target.checked)}
                      />
                      {t(
                        reserveTopup
                          ? 'dailySettlement.confirmTopup'
                          : externalTopup
                            ? 'cashWorkflow.confirmExternal'
                            : 'cashWorkflow.confirmPetty',
                      )}
                    </label>
                    <button
                      className={buttonClass}
                      disabled={
                        locked ||
                        !canEdit ||
                        !petty ||
                        !validMoney(pettyAmount) ||
                        Number(pettyAmount) <= 0 ||
                        (reserveTopup
                          ? !settlement ||
                            settlement.topupAmount === null ||
                            Number(pettyAmount) > Number(settlement.remainingRevenue)
                          : fundTransfer
                            ? !reserve || Number(pettyAmount) > Number(reserve.amount)
                            : !externalTopup && Number(pettyAmount) > Number(petty.amount)) ||
                        (externalTopup && (!sourceDescription.trim() || !topupNote.trim())) ||
                        !localNote ||
                        localNote.length > 255 ||
                        !pettyConfirmed
                      }
                      onClick={() =>
                        void submit(
                          reserveTopup
                            ? {
                                ...base(),
                                dailySettlementProtocol: 1,
                                action: 'CASH_REVENUE_HANDOFF',
                                destinationKind: 'RESERVE',
                                amount: pettyAmount,
                                confirmed: pettyConfirmed,
                                note: localNote,
                              }
                            : externalTopup
                              ? {
                                  ...base(),
                                  action: 'CASH_PETTY_EXTERNAL_TOPUP',
                                  locationId: petty!.id,
                                  amount: pettyAmount,
                                  sourceDescription: sourceDescription.trim(),
                                  evidenceRef: localNote,
                                  note: topupNote.trim(),
                                }
                              : purpose === 'FUND_TRANSFER_IN'
                                ? {
                                    ...base(),
                                    action: 'CASH_REPLENISH',
                                    fromLocationId: reserve!.id,
                                    toLocationId: petty!.id,
                                    amount: pettyAmount,
                                    note: localNote,
                                  }
                                : {
                                    ...base(),
                                    action: 'CASH_PETTY_TRANSACTION',
                                    locationId: petty!.id,
                                    amount: pettyAmount,
                                    purpose,
                                    evidenceRef: localNote,
                                    note: localNote,
                                  },
                        )
                      }
                    >
                      {t(
                        reserveTopup
                          ? 'dailySettlement.submitTopup'
                          : externalTopup
                            ? 'cashWorkflow.receiveExternal'
                            : fundTransfer
                              ? 'cashWorkflow.receiveFund'
                              : 'cashWorkflow.payPetty',
                      )}
                    </button>
                    {localNote.length > 255 && (
                      <p role="alert">{t('cashWorkflow.fundNoteTooLong')}</p>
                    )}
                    {(snap.pettyExternalTopups ?? [])
                      .filter((entry) => !entry.reversed)
                      .map((entry) => (
                        <div
                          key={entry.movementId}
                          role="group"
                          aria-label={`${entry.sourceDescription ?? ''} / ${entry.evidenceRef ?? ''} / ${entry.movementId}`}
                          className="mt-2 flex flex-wrap items-center gap-2 break-all"
                        >
                          <span>
                            {t('cashWorkflow.externalEntry', {
                              amount: entry.amount,
                              source: entry.sourceDescription ?? '—',
                              evidence: entry.evidenceRef ?? '—',
                              id: entry.movementId,
                            })}
                          </span>
                          <button
                            className={buttonClass}
                            disabled={locked || !topupNote.trim()}
                            onClick={() => {
                              if (
                                window.confirm(
                                  t('cashWorkflow.reverseExternalConfirm', {
                                    amount: entry.amount,
                                    source: entry.sourceDescription ?? '—',
                                    evidence: entry.evidenceRef ?? '—',
                                    id: entry.movementId,
                                  }),
                                )
                              )
                                void submit({
                                  ...base(),
                                  action: 'REVERSE_PETTY_EXTERNAL_TOPUP',
                                  originalMovementId: entry.movementId,
                                  note: topupNote.trim(),
                                });
                            }}
                          >
                            {t('cashWorkflow.reverseExternal')}
                          </button>
                        </div>
                      ))}
                    {(snap.pettyExternalTopups ?? []).some((entry) => !entry.reversed) &&
                      !externalTopup && (
                        <label className="block">
                          {t('cashWorkflow.externalNote')}
                          <input
                            className={`${inputClass} w-full`}
                            value={topupNote}
                            maxLength={255}
                            disabled={locked}
                            onChange={(e) => setTopupNote(e.target.value)}
                          />
                        </label>
                      )}
                  </section>
                </>
              )}
              {settling && (
                <>
                  {(settlement?.blockers ?? snap.blockers).length > 0 && (
                    <p role="alert">
                      {t('cashWorkflow.blockers', {
                        count: (settlement?.blockers ?? snap.blockers).length,
                      })}
                      :{' '}
                      {(settlement?.blockers ?? snap.blockers)
                        .map((b) => t(b.name, { defaultValue: b.name }))
                        .join(', ')}
                    </p>
                  )}
                  <p>{t('dailySettlement.physicalDepositHelp')}</p>
                  <label className="flex min-h-11 items-center gap-2">
                    <input
                      type="checkbox"
                      disabled={locked}
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                    />
                    {t('dailySettlement.confirmCounts')}
                  </label>
                  <button
                    className={`${buttonClass} bg-primary text-primary-content`}
                    disabled={
                      locked ||
                      !canClose ||
                      !balanced ||
                      !confirmed ||
                      !note.trim() ||
                      !settlement?.complete
                    }
                    onClick={() =>
                      void submit({
                        ...base(),
                        action: 'CLOSE_LOCATION_SESSION',
                        dailySettlementProtocol: 1,
                        counts: bags.map((b) => ({ locationId: b.id, amount: counts[b.id]! })),
                        confirmed,
                      })
                    }
                  >
                    {t('cashWorkflow.close')}
                  </button>
                </>
              )}
            </>
          )}
        </>
      )}
      {cash.intent && (
        <div className="space-x-3">
          <button className={buttonClass} disabled={cash.busy} onClick={() => void recover()}>
            {t('cashWorkflow.lookup')}
          </button>
          {cash.intent.body && cash.intent.stage !== 'COMMITTED' && (
            <button
              className={buttonClass}
              disabled={
                !parentReady || !settlementSnapshotReady || cash.busy || !owns(cash.intent.action)
              }
              onClick={() => void submit(cash.intent!.body as CashWorkflowWriteV1, cash.intent!)}
            >
              {t('cashWorkflow.retry')}
            </button>
          )}
          {cash.intent.stage === 'REJECTED' && (
            <button
              className={buttonClass}
              disabled={cash.busy || snapshot.isFetching}
              onClick={() =>
                void cash.run(async () => {
                  const record = cash.intent;
                  if (!record) return;
                  try {
                    await refreshResult();
                    if (
                      identityRef.current !== scope + identity ||
                      !cash.isCurrent(record.operationKey)
                    )
                      return;
                    cash.startNew();
                    setReadyKey('');
                    setReceipt(null);
                  } catch {
                    if (cash.isCurrent(record.operationKey)) cash.setError('REFRESH');
                  }
                })
              }
            >
              {t('cashWorkflow.next')}
            </button>
          )}
        </div>
      )}
      {receipt && <p role="status">{t('cashWorkflow.receipt', { id: receipt.operationId })}</p>}
      {receipt?.revenueHandoff && (
        <p role="status">
          {t('cashWorkflow.handoffReceipt', {
            amount: receipt.revenueHandoff.amount,
            destination: t(`cashWorkflow.bags.${receipt.revenueHandoff.destinationKind}`),
          })}
        </p>
      )}
      {receipt?.redemption && (
        <dl className="border border-base-300 rounded p-3">
          <dt>{t('cashRedemption.amount')}</dt>
          <dd>{receipt.redemption.amount}</dd>
          <dt>{t('cashRedemption.balance')}</dt>
          <dd>{receipt.redemption.appliedBalance}</dd>
          <dt>{t('cashRedemption.evidence')}</dt>
          <dd className="break-words">{receipt.redemption.evidenceRef}</dd>
        </dl>
      )}
      {receipt?.pettyExternalTopup && (
        <p role="status" className="break-words">
          {t(
            receipt.action === 'REVERSE_PETTY_EXTERNAL_TOPUP'
              ? 'cashWorkflow.externalReversalReceipt'
              : 'cashWorkflow.externalReceipt',
            {
              amount: receipt.pettyExternalTopup.amount,
              source: receipt.pettyExternalTopup.sourceDescription,
              evidence: receipt.pettyExternalTopup.evidenceRef,
            },
          )}
        </p>
      )}
      {receipt?.pettyTransaction && (
        <dl className="border border-base-300 rounded p-3">
          <dt>{t('cashWorkflow.purpose')}</dt>
          <dd>{t(`cashWorkflow.purpose_${receipt.pettyTransaction.purpose}`)}</dd>
          <dt>{t('cashWorkflow.amount')}</dt>
          <dd>NT${receipt.pettyTransaction.amount}</dd>
          <dt>{t('cashWorkflow.evidenceRef')}</dt>
          <dd className="break-words">{receipt.pettyTransaction.evidenceRef}</dd>
        </dl>
      )}
      {snap.status === 'CLOSED' && <p role="status">{t('cashWorkflow.closed')}</p>}
      {cash.error ? (
        <p role="alert">
          {t(cash.error === 'REFRESH' ? 'cashWorkflow.staleHelp' : 'cashWorkflow.operationFailed')}
        </p>
      ) : cash.intent?.stage === 'UNKNOWN' ? (
        <CashRecoveryNotice error={cash.error} />
      ) : null}
      <button
        className={buttonClass}
        disabled={cash.busy}
        onClick={() =>
          void (cash.intent?.stage === 'COMMITTED' ? recover() : cash.run(refreshResult))
        }
      >
        {t('cashWorkflow.refresh')}
      </button>
    </section>
  );
}
